import type { Dispatch } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { cardSurface } from '@/components/atoms/Card';
import { Radio, RadioGroup } from '@/components/atoms/RadioGroup';
import styles from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateModals.module.scss';
import type { OperateWizardAction } from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardReducer';

import type { SecondSourceCandidate } from './targetCandidates';

interface Props {
    secondSourceId: number | null;
    secondSourceCandidates: SecondSourceCandidate[];
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Empty-branch inline second-source picker. Spans the full grid width because it is a list of
 * choices rather than a paired form field. Only rendered when the operator has picked an empty
 * cage.
 */
export function SecondSourcePicker(props: Props) {
    const { secondSourceId, secondSourceCandidates, dispatch } = props;
    const { t } = useTranslation('fishGroups');
    const secondSourceLabelId = useId();

    return (
        <div className={classNames(styles.field, styles['field--fullWidth'])}>
            <span id={secondSourceLabelId} className={styles.label}>
                {t('actions.operate.splitAndMerge.target.secondSourceLabel')}
            </span>
            {secondSourceCandidates.length === 0 ? (
                <p className={styles.emptyText}>
                    {t('actions.operate.splitAndMerge.target.secondSourceEmpty')}
                </p>
            ) : (
                <RadioGroup
                    name="sam-second-source"
                    aria-labelledby={secondSourceLabelId}
                    value={secondSourceId == null ? '' : String(secondSourceId)}>
                    <ul className={styles.sourcesList}>
                        {secondSourceCandidates.map((candidate) => {
                            return (
                                <li
                                    key={candidate.fishGroupId}
                                    {...cardSurface(
                                        { radius: 'sm', padding: 'none', interactive: true },
                                        styles.sourceRow
                                    )}
                                    onClick={() => {
                                        dispatch({
                                            type: 'setSecondSource',
                                            fishGroupId: candidate.fishGroupId,
                                            name: candidate.name,
                                            currentCageId: candidate.cageId,
                                        });
                                    }}>
                                    <Radio value={String(candidate.fishGroupId)} />
                                    <span className={styles.sourceLabel}>
                                        {t('actions.operate.sources.candidateLabel', {
                                            name: candidate.name,
                                            cageName: candidate.cageName,
                                        })}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </RadioGroup>
            )}
        </div>
    );
}
